/**
 * Product Import API Client
 * Story Q-IMP-1: Beszállítói termékkatalógus XLSX import
 *
 * M-009 fix: Migrated from raw fetch() to centralized ApiClient
 * (proper auth headers, token refresh, offline awareness)
 */

import { api } from './client';

// ============================================
// TYPES
// ============================================

export interface ColumnMapping {
  name: string;
  price: string;
  sku?: string;
  brand?: string;
  category?: string;
  supplierSku?: string;
  barcode?: string;
  description?: string;
  model?: string;
  unit?: string;
  vatPercent?: string;
  listPrice?: string;
  sellingPrice?: string;
}

export interface XlsxPreview {
  headers: string[];
  sampleRows: string[][];
  totalRows: number;
}

export interface ImportSummary {
  imported: number;
  updated: number;
  skipped: number;
  errors: string[];
  totalRows: number;
}

export interface SavedMapping {
  mapping: ColumnMapping | null;
  lastImportAt: string | null;
  lastImportCount: number;
}

// ============================================
// PRODUCT FIELDS for mapping UI
// ============================================

export const PRODUCT_FIELDS: Array<{
  key: keyof ColumnMapping;
  label: string;
  required: boolean;
}> = [
  { key: 'name', label: 'Terméknév', required: true },
  { key: 'price', label: 'Beszerzési ár', required: true },
  { key: 'sku', label: 'Cikkszám (SKU)', required: false },
  { key: 'brand', label: 'Márka', required: false },
  { key: 'category', label: 'Kategória', required: false },
  { key: 'supplierSku', label: 'Beszállítói cikkszám', required: false },
  { key: 'barcode', label: 'Vonalkód', required: false },
  { key: 'description', label: 'Leírás', required: false },
  { key: 'model', label: 'Modell', required: false },
  { key: 'unit', label: 'Mértékegység', required: false },
  { key: 'vatPercent', label: 'ÁFA %', required: false },
  { key: 'listPrice', label: 'Listaár', required: false },
  { key: 'sellingPrice', label: 'Eladási ár', required: false },
];

// ============================================
// API FUNCTIONS (M-009 fix: uses centralized ApiClient)
// ============================================

/**
 * Upload XLSX and get preview (headers + sample rows)
 */
export async function previewXlsx(file: File): Promise<{ data: XlsxPreview }> {
  const formData = new FormData();
  formData.append('file', file);
  return api.upload<{ data: XlsxPreview }>('/products-direct/import/preview', formData);
}

/**
 * Execute XLSX import with column mapping
 */
export async function importXlsx(
  file: File,
  supplierId: string,
  mapping: ColumnMapping,
  updateExisting: boolean
): Promise<{ data: ImportSummary }> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('supplierId', supplierId);
  formData.append('mapping', JSON.stringify(mapping));
  formData.append('updateExisting', String(updateExisting));
  return api.upload<{ data: ImportSummary }>('/products-direct/import', formData);
}

/**
 * Get saved mapping for a supplier
 */
export async function getSavedMapping(supplierId: string): Promise<{ data: SavedMapping }> {
  return api.get<{ data: SavedMapping }>(`/products-direct/import/mapping/${supplierId}`);
}

/**
 * Save mapping for a supplier
 */
export async function saveMappingForSupplier(
  supplierId: string,
  mapping: ColumnMapping
): Promise<{ success: boolean }> {
  return api.post<{ success: boolean }>(`/products-direct/import/mapping/${supplierId}`, {
    mapping,
  });
}
